import * as yup from 'yup';
import {
  isValidCheckSum,
  rsaIdRegex,
  postalCodeRegex
} from '../../../constants';

import dayjs from 'dayjs';

const DATE_FORMAT = 'YYYY-MM-DD';
const today = dayjs();

const minDOB = today.subtract(100, 'year'); // Max age: 100 years
const maxDOB = today.subtract(18, 'year'); // Min age: 18 years

const yyyymmddRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
const yyyymdRegex = /^\d{4}-([1-9]|1[0-2])-([1-9]|[12][0-9]|3[01])$/;

export const PersonalDetailsSchema = yup.object().shape({
  idNumber: yup.string().when('idType', {
    is: 'id',
    then: () => yup.string().matches(rsaIdRegex, 'SA ID Number is not valid')
      .test('idNumber', 'SA ID Number is not valid', (item) => isValidCheckSum(item))
      .required('SA ID Number is a required field'),
    otherwise: () => yup.string().nullable().optional()
  }),
  passportNumber: yup.string().when('idType',
    {
      is: 'pp',
      then: () => yup.string().required('Passport number is a required field'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  whenMovedIn: yup
    .string()
    .required('When did you move to this address date is a required field')
    .test('min-date', 'Invalid When moved in: Cannot be greater than 100yrs old', value => {
      const parsed = dayjs(value, DATE_FORMAT, true);
      return parsed.isValid() && parsed.isAfter(minDOB);
    })
    .test('max-date', 'Invalid When moved in: Date should be in the past', value => {
      const parsed = dayjs(value, DATE_FORMAT, true);
      return parsed.isValid() && parsed.isBefore(today);
    })
    .test('valid-format', 'Invalid When moved in: Please capture correct date', value => {
      const [year, month, day] = value.split('-').map(Number);
      if (!yyyymdRegex.test(`${year}-${month}-${day}`)) {
        return false;
      }
      return yyyymmddRegex.test(value) && dayjs(value, DATE_FORMAT, true).isValid();
    }),

  flatUnitNumber: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.number()
      .typeError('Flat/Unit/Floor must be a number')
      .positive('Flat/Unit/Floor must be positive')
      .integer('Flat/Unit/Floor must be a whole number')
      .nullable(),
    otherwise: () => yup.string().nullable().optional()
  }),
  townUnitNumber: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.number()
        .typeError('Unit or House number must be a number')
        .positive('Unit or House number must be positive')
        .integer('Unit or House number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeUnitNumber: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.number()
        .typeError('Unit or Suite number must be a number')
        .positive('Unit or Suite number must be positive')
        .integer('Unit or Suite number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  houseUnitNumber: yup.string().when('buildingType',
    {
      is: 'House',
      then: () => yup.number()
        .typeError('Unit or House number must be a number')
        .positive('Unit or House number must be positive')
        .integer('Unit or House number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  flatBuilding: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.string()
      .required('Building Name is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  townBuilding: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.string()
        .required('Estate Name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeBuilding: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.string()
        .required('Building or Office park name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  houseStreet: yup.string().when('buildingType', {
    is: 'House',
    then: () => yup.string()
      .required('Street Name is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  flatStreet: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.string()
      .required('Address is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  townStreet: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.string()
        .required('Street Name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeStreet: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.string()
        .required('Address is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  suburb: yup.string()
    .required('Suburb is required'),

  title: yup.string()
    .required('Title is required'),

  positionInCompany: yup.string()
    .required('Position in Company is required'),

  residentialStatus: yup.string()
    .required('Residential Status is required'),

  idType: yup.string()
    .required('Id Type is required'),

  provinces: yup.string()
    .required('Provinces is required'),

  city: yup.string()
    .required('City is required'),

  buildingType: yup.string()
    .required('Building Type is required'),

  postalCode: yup.string()
    .matches(postalCodeRegex, 'Postal Code must be 4 digits')
    .required('Postal Code is required'),

  gender: yup.string().when('idType', ([idType], schema) =>
    idType !== 'id'
      ? schema.required('Gender is required')
      : schema.nullable().optional()
  ),

  dateOfBirth: yup.string().when('idType', ([idType], schema) =>
    idType !== 'id'
      ? schema
        .required('Date of Birth is a required')
        .test('min-age', 'Invalid Date of Birth: Cannot be greater than 100yrs old', value => {
          const dob = dayjs(value, DATE_FORMAT, true);
          return dob.isValid() && dob.isAfter(minDOB);
        })
        .test('max-age', 'Invalid Date of Birth: must be 18 years or older', value => {
          const dob = dayjs(value, DATE_FORMAT, true);
          return dob.isValid() && dob.isBefore(maxDOB);
        })
        .test('max-date', 'Invalid Date of Birth: Date should be in the past', value => {
          const parsed = dayjs(value, DATE_FORMAT, true);
          return parsed.isValid() && parsed.isBefore(today);
        })
        .test('valid-format', 'Invalid Date of Birth: Please capture correct date', value => {
          const [year, month, day] = value.split('-').map(Number);
          if (!yyyymdRegex.test(`${year}-${month}-${day}`)) {
            return false;
          }
          return yyyymmddRegex.test(value) && dayjs(value, DATE_FORMAT, true).isValid();
        })
      : schema.nullable().optional()
  ),

  passportCountry: yup.string().when('idType', ([idType], schema) =>
    idType !== 'id'
      ? schema
        .required('Country of Issue is a required')
      : schema.nullable().optional()
  )
});